/** @jsxImportSource @emotion/react */
"use client";
import { forwardRef, type JSX } from "react";

import { spacing } from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { KeyedElement } from "../../../../types/keyedElement.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import type { AvatarCommunityProps } from "../AvatarCommunity/AvatarCommunity.emotion";
import { AvatarCommunity } from "../AvatarCommunity/AvatarCommunity.emotion";
import { Text } from "../Text/Text.emotion";

const hallowElement: HallowElement = "div";

const determinedElementStyle = {
  alignItems: "center",
  display: "flex",
  gap: spacing.ms,
  width: spacing.full,
};
const infoStyle = {
  display: "flex",
  flexDirection: "column" as "column",
  flexGrow: 1,
  gap: spacing.t,
  minWidth: spacing.none,
};
const avatarStyle = {
  flexShrink: 0,
};

export type ClassroomProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  avatarProps?: AvatarCommunityProps;
  name?: string;
  rightActions?: KeyedElement | KeyedElement[];
};

export const Classroom = forwardRef<any, ClassroomProps>(
  (
    { asChild = false, avatarProps, children, name, rightActions, ...props },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const determinedChildren = determineChildrenInject({
      afterChildrenInject: [
        <AvatarCommunity key="avatar" css={avatarStyle} {...avatarProps} />,
        <div key="info" css={infoStyle}>
          <Text overflow="ellipsis" size="s" type="title">
            {name}
          </Text>
        </div>,
        rightActions as JSX.Element,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement ref={ref} css={determinedElementStyle} {...props}>
        {determinedChildren}
      </DeterminedElement>
    );
  },
);

Classroom.displayName = "Author";
